import { useEffect, useRef, useState } from 'react';

/**
 * =============================================================================
 * useDebouncedValue — let typing finish before anything acts on it
 * =============================================================================
 *
 * WHY THIS EXISTS. Filter controls in this CMS commit on every keystroke, and
 * each commit is a request. Typing «2026» into the publication-year box sent
 * four: `PublicationYearFrom=2`, `=20`, `=202`, `=2026`. The API answered the
 * first three with HTTP 400 — correctly, those are not years — so the operator
 * watched the table error three times on the way to a result that was right
 * all along.
 *
 * The same shape of waste sits behind the search box: one request per letter
 * of an Arabic name, every one of them already stale by the time it lands.
 *
 * WHAT IT DOES. Holds the live value back until the operator stops typing for
 * `delay`, then releases it once.
 *
 * TWO DETAILS THAT MATTER
 *
 *  - AN EMPTY VALUE IS RELEASED IMMEDIATELY. Clearing a filter is not typing,
 *    it is a decision, and making someone wait 400ms to watch a filter come
 *    off is the kind of lag that reads as a broken screen.
 *  - A VALUE THAT ARRIVES ALREADY EQUAL to what is published does not restart
 *    the timer, so an unrelated re-render cannot hold a pending value hostage.
 *
 * @param {*} value      the live value, changing on every keystroke
 * @param {number} delay quiet period in ms before it is released
 * @returns {*} the settled value
 */
const useDebouncedValue = (value, delay = 400) => {
  const [settled, setSettled] = useState(value);
  const settledRef = useRef(value);

  useEffect(() => {
    if (value === settledRef.current) return undefined;

    // Clearing is a decision, not typing — no reason to make it wait.
    if (value === '' || value === null || value === undefined) {
      settledRef.current = value;
      setSettled(value);
      return undefined;
    }

    const timer = setTimeout(() => {
      settledRef.current = value;
      setSettled(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
};

export default useDebouncedValue;
